import "./FAQ.css";
import { useState, useRef } from "react";

function FAQ({ open, onClose }) {

  // ================= DRAG SYSTEM =================
  const [pos, setPos] = useState({ x: 250, y: 80 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });

  const startDrag = (e) => {
    dragging.current = true;
    offset.current = {
      x: e.clientX - pos.x,
      y: e.clientY - pos.y,
    };
  };

  const onDrag = (e) => {
    if (!dragging.current) return;

    setPos({
      x: e.clientX - offset.current.x,
      y: e.clientY - offset.current.y,
    });
  };

  const stopDrag = () => {
    dragging.current = false;
  };

  // ================= FAQ DATA =================
  const faqData = [
    {
    q: "Where should I begin when starting the refinery simulation?",
    a: `Begin at the Marine Terminal or Pipeline Import page, depending on your crude oil source.

    Verify that crude is available before attempting to transfer it into the refinery.

    Once crude transfer is confirmed, proceed to the crude storage tanks, then to the blending section before starting the preheat train.

    Following the refinery process sequence helps maintain realistic operation throughout the Digital Twin.`
},

{
    q: "What is the purpose of the Marine Terminal page?",
    a: `The Marine Terminal represents crude oil receipt from export vessels and tankers.

    Operators use this page to monitor incoming crude transfers, transfer rates and storage allocation.

    It serves as the starting point for crude receipt into the refinery.

    All crude received here is routed to designated storage tanks before processing.`
},

{
    q: "How do I receive crude from the Marine Terminal?",
    a: `First confirm that the receiving storage tank has available capacity.

    Open the required transfer valves and start the transfer pumps.

    Monitor flowrate, pressure and tank level continuously during transfer.

    Once the desired volume has been received, stop the pumps before closing the transfer valves.`
},

{
    q: "What is Pipeline Import used for?",
    a: `Pipeline Import simulates crude oil arriving through transmission pipelines.

    Operators monitor incoming flowrate, pressure and transfer conditions before directing crude into storage tanks.

    This page demonstrates continuous crude receipt without marine transportation.

    Pipeline operations follow the same storage principles as marine receipt.`
},

{
    q: "How does Truck Offloading work?",
    a: `Truck Offloading represents crude or intermediate products arriving by road tanker.

    Before unloading, operators verify product type, available storage capacity and line alignment.

    Transfer pumps are then started while flow and tank levels are monitored.

    Once unloading is complete, the transfer system is safely isolated before disconnecting the truck.`
},

{
    q: "Why are crude storage tanks important?",
    a: `Crude storage tanks provide inventory between crude receipt and refinery processing.

    They allow the refinery to continue operating even if crude deliveries are temporarily interrupted.

    Storage tanks also provide time for crude quality verification before processing.

    Proper tank management improves refinery reliability and scheduling flexibility.`
},

{
    q: "How do I transfer crude from storage tanks?",
    a: `Select the desired crude storage tank.

    Open the outlet valves and verify that the destination line is available.

    Start the transfer pumps while monitoring flowrate and pressure.

    Continue transferring until the required inventory has been delivered to the next process unit.`
},

{
    q: "What is the purpose of the blending tanks?",
    a: `Blending tanks combine different crude oils to achieve the desired refinery feed quality.

    Operators can adjust blend ratios based on API gravity, sulfur content or operational requirements.

    The blended crude becomes the final feed sent to downstream refinery units.

    Proper blending improves refinery efficiency and product quality.`
},

{
    q: "How do I create a crude blend?",
    a: `Transfer crude from two or more storage tanks into the blending system.

    Adjust the contribution of each crude using the transfer controls.

    Observe the calculated blend properties as the Digital Twin updates the mixture automatically.

    Once the desired blend is achieved, transfer it to the day tank or directly to the process units.`
},

{
    q: "What is the function of the Day Tank?",
    a: `The Day Tank serves as the immediate feed source for refinery processing.

    It provides stable inventory for the crude charge pumps.

    Maintaining adequate day tank level prevents interruptions to refinery operation.

    It acts as a buffer between crude storage and continuous refinery processing.`
},

{
    q: "Why is the Day Tank level important?",
    a: `Low day tank level can interrupt crude feed to the refinery.

    Excessively high level may reduce surge capacity for incoming crude.

    Operators should maintain the level within the recommended operating range.

    Stable inventory supports continuous and reliable refinery operation.`
},

{
    q: "What is the purpose of the Slop Tank?",
    a: `The Slop Tank collects off-specification hydrocarbons, drain oils and recovered products.

    Instead of being wasted, these materials may be reprocessed later.

    This improves refinery product recovery while reducing hydrocarbon losses.

    Slop management is an important part of refinery efficiency and environmental protection.`
},

{
    q: "When should material be sent to the Slop Tank?",
    a: `Material should be routed to the Slop Tank whenever it does not meet product specifications.

    Drain operations, equipment maintenance and startup products are common examples.

    Off-specification streams can later be blended back into the process where appropriate.

    This minimizes product losses during refinery operation.`
},

{
    q: "What is the Preheat Train?",
    a: `The Preheat Train is a series of heat exchangers that recover heat from hot refinery streams.

    Instead of sending cold crude directly to the furnace, it is gradually heated using recovered process heat.

    This significantly reduces furnace fuel consumption.

    Energy recovery within the preheat train improves overall refinery efficiency.`
},

{
    q: "Why is the Preheat Train operated before the furnace?",
    a: `Heating crude using recovered process energy is much more economical than heating entirely with fuel gas.

    The furnace only supplies the remaining heat required for distillation.

    This reduces operating cost while improving thermal efficiency.

    Every refinery uses heat recovery whenever possible before direct firing.`
},

{
    q: "How do I know the Preheat Train is working correctly?",
    a: `Monitor crude inlet and outlet temperatures across each exchanger.

    Heat exchanger effectiveness should remain within expected operating limits.

    Reduced temperature gain may indicate fouling or reduced heat transfer.

    Continuous monitoring helps maintain refinery energy efficiency.`
},

{
    q: "What is the purpose of the Furnace page?",
    a: `The furnace provides the final heating required before crude enters the atmospheric distillation column.

    It raises crude temperature to the required separation temperature.

    Furnace performance directly affects distillation efficiency.

    Operators must carefully monitor outlet temperature to avoid overheating.`
},

{
    q: "Why should furnace temperature not be increased excessively?",
    a: `Excessive furnace temperatures may cause thermal cracking inside the heater tubes.

    Overheating also increases fuel consumption and equipment stress.

    Maintaining the correct outlet temperature improves product yield and equipment life.

    Safe furnace operation is critical to refinery performance.`
},

{
    q: "What happens after the furnace?",
    a: `Heated crude enters the Atmospheric Distillation Unit.

    Inside the column, hydrocarbons separate according to their boiling points.

    Products leave the column through different side draws and overhead systems.

    This forms the basis of crude oil refining.`
},

{
    q: "What is the Atmospheric Distillation Unit (ADU)?",
    a: `The ADU is the primary separation unit of the refinery.

    It separates crude oil into gases, naphtha, kerosene, diesel and atmospheric residue.

    Nearly every downstream refinery process begins with the ADU.

    It is often called the heart of the refinery.`
},

{
    q: "Why does the simulator include laboratory reports for each unit?",
    a: `Each laboratory report evaluates the performance of an individual refinery unit.

    Reports summarize operating conditions, compositions, recoveries, efficiencies and product qualities.

    They provide engineers with detailed information similar to refinery laboratory documentation.

    This supports process analysis and operator training.`
},

{
    q: "What is the Stabilizer Tower used for?",
    a: `The Stabilizer Tower removes light hydrocarbons from unstabilized naphtha.

    This reduces product vapor pressure before storage.

    Stable naphtha is safer to transport and meets product specifications.

    The tower also recovers valuable LPG components.`
},

{
    q: "Why are Diesel and Kerosene Strippers required?",
    a: `Stripper columns remove dissolved light hydrocarbons remaining in diesel and kerosene.

    Steam or reboiler heat assists the stripping process.

    This improves flash point and overall product quality.

    Proper stripping ensures refinery products meet required specifications.`
},

{
    q: "What should I monitor while operating the Diesel Stripper?",
    a: `Monitor stripping steam flow, feed temperature, bottom product quality and overhead vapor generation.

    Steam ratio is one of the most important operating parameters.

    Excess steam wastes energy while insufficient steam reduces stripping efficiency.

    Proper balance produces high-quality diesel.`
},

{
    q: "What should I monitor in the Kerosene Stripper?",
    a: `Observe reboiler duty, bottom temperature, overhead flow and kerosene recovery.

    Stable heat input is essential for consistent product quality.

    Sudden temperature changes may affect flash point and product specifications.

    Continuous monitoring helps maintain efficient operation.`
},

{
    q: "Why are equipment faceplates provided?",
    a: `Equipment faceplates allow users to inspect and interact with individual process equipment.

    They display operating parameters such as flow, pressure, temperature, status and alarms.

    Operators can understand how each equipment item contributes to the overall refinery process.

    Faceplates closely resemble those used in industrial DCS systems.`
},

{
    q: "What should I do before starting any pump?",
    a: `Verify that suction valves are open and adequate liquid is available.

    Confirm the discharge line is correctly aligned.

    Ensure downstream equipment is ready to receive flow.

    Only then should the pump be started while monitoring pressure and flow.`
},

{
    q: "Why is valve alignment important?",
    a: `Incorrect valve alignment can prevent flow or direct products to the wrong destination.

    Proper alignment ensures safe transfer of hydrocarbons throughout the refinery.

    Operators should always confirm the process route before starting equipment.

    Valve position verification is a fundamental refinery operating practice.`
},

{
    q: "How should I operate the refinery during training?",
    a: `Operate the refinery exactly as you would a real process plant.

    Start from crude receipt, continue through storage, blending, preheating, furnace operation, atmospheric distillation and product treating.

    Monitor alarms, process variables and laboratory reports throughout the simulation.

    Following the actual refinery process sequence provides the most realistic learning experience.`
},

{
    q: "What should I check before opening the Marine Terminal page?",
    a: `Before opening the Marine Terminal, verify that a receiving crude storage tank has sufficient available capacity.

    Ensure transfer pumps are available and that the transfer route has been properly aligned.

    Confirm that no conflicting transfer operation is already in progress.

    These checks help simulate safe crude receipt operations.`
},

{
    q: "How do I know which crude tank should receive incoming crude?",
    a: `The selected storage tank should have enough free capacity and should be designated for the incoming crude grade.

    Operators should avoid mixing incompatible crude grades unless blending is intended.

    The Digital Twin allows users to monitor tank inventory before initiating transfer.

    Proper tank allocation improves inventory management and blending flexibility.`
},

{
    q: "Can I receive crude from both Marine Terminal and Pipeline Import simultaneously?",
    a: `Yes, provided sufficient storage capacity exists and the transfer routes do not conflict.

    Multiple crude receipt operations are common in refinery logistics.

    Operators should carefully monitor total inflow to prevent tank overfilling.

    Inventory management becomes especially important during simultaneous transfers.`
},

{
    q: "What happens if a storage tank becomes full?",
    a: `Once a storage tank reaches its operating capacity, additional crude transfer should be stopped immediately.

    Continuing transfer could result in overfill conditions and operational alarms.

    The Digital Twin demonstrates proper inventory management by encouraging operators to monitor tank levels continuously.

    Always redirect incoming crude to another available tank before reaching maximum capacity.`
},

{
    q: "Why are crude transfer pumps separated from storage tanks?",
    a: `Transfer pumps provide controlled movement of crude between different refinery sections.

    Separating storage from pumping allows greater operational flexibility.

    Pumps can transfer crude to blending tanks, day tanks or other storage locations as required.

    This reflects actual refinery logistics systems.`
},

{
    q: "When should crude blending be performed?",
    a: `Blending should normally be completed before crude enters the day tank or crude charge pumps.

    This ensures that a consistent refinery feed is supplied to downstream units.

    Stable feed properties improve furnace operation and distillation performance.

    Blending before processing also improves product consistency.`
},

{
    q: "Why does the Digital Twin calculate blend properties automatically?",
    a: `The Digital Twin continuously evaluates the properties of each crude entering the blend.

    As blend ratios change, important characteristics such as density and composition are recalculated automatically.

    This demonstrates how refinery blending affects downstream processing.

    Operators can immediately observe the impact of blend adjustments.`
},

{
    q: "What is the function of the Crude Charge Pump page?",
    a: `Crude charge pumps deliver blended crude from the day tank into the refinery process units.

    They maintain the required feed flowrate to the preheat train.

    Stable pump operation ensures continuous refinery production.

    Pump performance directly influences refinery throughput.`
},

{
    q: "Why is pump suction pressure important?",
    a: `Adequate suction pressure prevents cavitation inside the pump.

    Low suction pressure can damage pump components and reduce pumping efficiency.

    Operators should verify sufficient liquid level and proper suction valve alignment before startup.

    Stable suction conditions improve pump reliability.`
},

{
    q: "What is the purpose of the Crude Preheat Exchangers?",
    a: `Crude preheat exchangers recover thermal energy from hot refinery streams.

    They transfer this energy to incoming crude before furnace heating.

    This reduces fuel consumption while increasing refinery thermal efficiency.

    Heat recovery is one of the most important energy-saving processes in refinery operation.`
},

{
    q: "Why are multiple heat exchangers used instead of one?",
    a: `Several exchangers gradually increase crude temperature using different hot process streams.

    This maximizes heat recovery throughout the refinery.

    Multiple exchangers also improve operational flexibility during maintenance.

    The arrangement closely reflects actual refinery preheat trains.`
},

{
    q: "What should I monitor while operating the preheat train?",
    a: `Observe inlet temperature, outlet temperature, pressure drop and exchanger effectiveness.

    Unexpected temperature loss may indicate fouling or reduced heat transfer.

    Maintaining efficient heat recovery reduces furnace fuel requirements.

    Continuous monitoring supports energy optimization.`
},

{
    q: "Why is pressure monitored throughout the refinery?",
    a: `Pressure is one of the most important operating variables in refinery processing.

    It influences boiling points, separation efficiency, equipment safety and process stability.

    Sudden pressure changes may indicate equipment malfunction or process disturbances.

    The Digital Twin continuously updates pressure throughout the refinery.`
},

{
    q: "What should I do if a pressure alarm occurs?",
    a: `First identify which equipment generated the alarm.

    Review upstream and downstream operating conditions before taking corrective action.

    Check pumps, valves and flowrates for abnormalities.

    The simulator encourages systematic troubleshooting rather than immediate corrective action.`
},

{
    q: "Why are temperatures different at each process unit?",
    a: `Each refinery process performs a different thermal function.

    Furnaces increase temperature while condensers, coolers and heat exchangers remove heat.

    Separation columns also maintain temperature gradients to achieve product separation.

    These variations are essential for efficient refinery operation.`
},

{
    q: "Why are some process streams recycled?",
    a: `Recycling valuable hydrocarbons improves refinery recovery.

    Instead of losing useful products, recycle streams are returned to earlier process units for additional separation.

    This increases overall refinery efficiency.

    Recycling also minimizes hydrocarbon losses.`
},

{
    q: "What is the purpose of reflux in distillation columns?",
    a: `Reflux improves separation by returning condensed liquid back into the column.

    This increases contact between vapor and liquid phases.

    Better contact improves product purity.

    Proper reflux control is essential for stable refinery operation.`
},

{
    q: "Why is overhead condensation necessary?",
    a: `Hot vapors leaving the top of the column must be cooled before storage or further processing.

    Condensation converts vapor into liquid product.

    This allows separation of gases from liquid hydrocarbons.

    Condensers therefore play a critical role in refinery operation.`
},

{
    q: "What is the function of the Reflux Drum?",
    a: `The reflux drum separates condensed liquid from non-condensable gases.

    Part of the liquid returns to the column as reflux.

    The remaining liquid becomes product.

    Gas leaves through the overhead gas system.`
},

{
    q: "Why are transmitters located throughout the refinery?",
    a: `Transmitters continuously measure process variables such as pressure, temperature, level and flow.

    These measurements allow operators to monitor plant performance.

    The DCS uses transmitter values for process control and alarm generation.

    Reliable measurements are fundamental to refinery operation.`
},

{
    q: "How do I interpret equipment status colors?",
    a: `Equipment colors provide quick visual indication of operating status.

    Green generally represents normal operation.

    Yellow indicates warning conditions.

    Red highlights alarms or abnormal operating situations requiring operator attention.`
},

{
    q: "Why do flowrates change continuously?",
    a: `Flowrates naturally fluctuate as process conditions evolve.

    Pump performance, valve position, product demand and equipment interaction all influence flow.

    The Digital Twin reproduces these variations to simulate realistic refinery operation.

    Stable operation minimizes unnecessary fluctuations.`
},

{
    q: "Why are some valves motor-operated while others are manual?",
    a: `Motor-operated valves can be controlled remotely through the DCS.

    Manual valves generally require field operation.

    The Digital Twin includes both types to reflect actual refinery equipment.

    Understanding valve function improves operational awareness.`
},

{
    q: "What should I check before opening a valve?",
    a: `Confirm that the downstream destination is ready to receive flow.

    Verify line pressure and equipment status.

    Ensure opening the valve will not conflict with another transfer operation.

    Proper valve operation helps prevent process upsets.`
},

{
    q: "How do I know if a pump is overloaded?",
    a: `Abnormal discharge pressure, unstable flow or excessive operating conditions may indicate overload.

    Pump alarms may also be generated.

    Operators should investigate the process rather than immediately restarting equipment.

    The Digital Twin demonstrates these operating principles.`
},

{
    q: "Why are production KPIs displayed on multiple pages?",
    a: `Each process unit contributes differently to overall refinery performance.

    Displaying KPIs locally allows operators to evaluate individual equipment.

    Overall production dashboards combine these values into plant-wide performance indicators.

    This supports operational decision-making.`
},

{
    q: "Can I study process interactions between different refinery units?",
    a: `Yes.

    One of the strengths of the Digital Twin is demonstrating how upstream changes affect downstream units.

    Operators can observe how crude quality, furnace temperature or reflux changes influence overall refinery performance.

    This promotes system-wide process understanding.`
},

{
    q: "What is the purpose of the Production Summary page?",
    a: `Production Summary combines information from every refinery process unit.

    It provides total feed, product generation, equipment utilization and production performance.

    Operators use it as the primary operational dashboard.

    It represents the refinery's overall operating condition at a glance.`
},

{
    q: "Why are laboratory reports generated automatically after operation?",
    a: `The laboratory reports summarize everything that occurred during refinery operation.

    They include operating conditions, recoveries, efficiencies, product compositions and engineering calculations.

    This allows operators to evaluate plant performance after completing a simulation.

    The reports closely resemble refinery operational documentation.`
},

{
    q: "How can I become proficient using the NK Refinery Digital Twin?",
    a: `Follow the actual refinery process sequence every time you operate the simulator.

    Begin with crude receipt, continue through storage, blending, preheating, furnace operation, atmospheric distillation and downstream treating units.

    Study the laboratory reports after each run and compare operational performance.

    Repeating complete refinery operating cycles is the most effective way to master the Digital Twin.`
},
    {
    q: "What units are available in the NK Refinery Digital Twin?",
    a: `The NK Refinery Digital Twin contains multiple process units that closely represent a modular crude oil refinery.

    These include crude storage tanks, crude transfer systems, crude blending, preflash tower, atmospheric distillation unit (ADU), stabilization tower, diesel stripper, kerosene stripper, furnaces, heat exchangers, pumps, condensers, reflux drums, storage tanks, flare systems and utility systems.

    Each unit is interconnected to simulate realistic refinery process flow and equipment interaction.

    Users can study how changes in one process unit influence downstream equipment throughout the refinery.`
},

{
    q: "Does the simulator calculate process compositions automatically?",
    a: `Yes. Product compositions are generated dynamically based on process operating conditions.

    As temperatures, pressures, flowrates and operating efficiencies change, the hydrocarbon compositions are recalculated automatically.

    This allows users to observe how refinery operating conditions influence final product quality.

    The calculations are performed entirely within the Digital Twin environment for training and educational purposes.`
},

{
    q: "Why do product qualities change during operation?",
    a: `Product quality changes because refinery separation efficiency depends on process conditions.

    Variations in furnace outlet temperature, reflux ratio, pressure, stripping steam and feed composition all influence the final product specifications.

    The simulator continuously recalculates these changes to demonstrate realistic refinery behaviour.

    This allows operators to understand the relationship between process control and product quality.`
},

{
    q: "Can I start or stop refinery equipment?",
    a: `Yes. Operators can start and stop pumps, open or close valves and observe how the refinery responds.

    Every equipment action affects downstream process conditions exactly as expected in an operating refinery.

    These interactions provide practical understanding of refinery operating procedures without affecting any physical equipment.

    The system is intended solely for simulation and operator training.`
},

{
    q: "Why do alarms appear automatically?",
    a: `The Digital Twin continuously monitors process variables against predefined operating limits.

    When temperatures, pressures, flowrates or equipment conditions exceed safe limits, alarms are generated automatically.

    This helps trainees learn how refinery alarm systems function and how abnormal operating conditions should be recognized.

    Alarm behaviour closely resembles that of modern refinery Distributed Control Systems.`
},

{
    q: "Can the Digital Twin simulate equipment failures?",
    a: `Yes. The simulator can represent abnormal operating conditions including equipment trips, blocked lines, high temperatures, pressure deviations and flow interruptions.

    These scenarios allow operators to practise troubleshooting without exposing personnel or equipment to risk.

    Fault simulation is an important part of competency development for refinery operations.

    All scenarios remain entirely within the virtual environment.`
},

{
    q: "What laboratory reports are available?",
    a: `The Digital Twin automatically generates comprehensive laboratory reports for major refinery units.

    Reports include feed analysis, product quality, separation performance, energy efficiency, equipment performance, recovery calculations and operational KPIs.

    Every report is generated from the current simulated operating conditions.

    This allows users to evaluate refinery performance just as they would using a real refinery laboratory information system.`
},

{
    q: "Can product recovery be monitored?",
    a: `Yes. The Digital Twin continuously calculates product recovery throughout the refinery.

    Recovery calculations are based on inlet flowrates, outlet production, stripping performance and separation efficiencies.

    These values help operators determine how efficiently valuable hydrocarbons are being recovered.

    Recovery trends are updated automatically as operating conditions change.`
},

{
    q: "How is energy efficiency calculated?",
    a: `Energy efficiency is determined from the operating performance of furnaces, heat exchangers, condensers and reboilers.

    Heat transfer effectiveness and equipment operating conditions are continuously evaluated.

    The resulting efficiency values provide an indication of how effectively thermal energy is being utilized.

    These calculations support process optimization and operational awareness.`
},

{
    q: "Can the simulator generate production reports?",
    a: `Yes. Production reports summarize refinery throughput, product generation and equipment performance.

    Reports include feed rates, product flowrates, operating efficiencies and production balances.

    Operators can use these reports to evaluate refinery performance over time.

    All reports are generated automatically from simulated process data.`
},

{
    q: "What engineering calculations are performed automatically?",
    a: `The Digital Twin performs numerous engineering calculations during operation.

    These include material balance, component distribution, separation efficiency, heat duty, recovery efficiency, stripping performance, product composition and equipment performance calculations.

    Every calculation updates automatically as process variables change.

    This provides a realistic engineering environment for training and analysis.`
},

{
    q: "Can refinery startup and shutdown be demonstrated?",
    a: `Yes. The simulator can demonstrate the gradual startup and shutdown of refinery process units.

    Operators can observe how temperatures, pressures and product flows evolve during these operations.

    Startup sequences help users understand the importance of following proper refinery operating procedures.

    Shutdown simulations also demonstrate how process units safely return to standby conditions.`
},

{
    q: "Why do temperatures vary throughout the refinery?",
    a: `Temperature changes are expected because every refinery unit performs a different process function.

    Furnaces increase process temperatures while heat exchangers, condensers and coolers remove heat.

    Separation columns also experience temperature gradients from top to bottom.

    The Digital Twin reproduces these thermal changes to closely represent actual refinery operation.`
},

{
    q: "How realistic are the process calculations?",
    a: `The Digital Twin is developed using engineering principles commonly applied in refinery operations.

    Equipment behaviour is based on process relationships between temperature, pressure, flow, heat transfer and hydrocarbon separation.

    Although simplified for simulation purposes, the behaviour closely resembles real refinery operating conditions.

    This provides an effective platform for process learning and operational familiarization.`
},

{
    q: "Can engineering students use the simulator?",
    a: `Absolutely.

    Engineering students can use the Digital Twin to understand refinery operations, instrumentation, process control and equipment interaction.

    It bridges the gap between theoretical classroom knowledge and practical industrial operation.

    Students gain valuable exposure to refinery processes before entering actual industrial facilities.`
},

{
    q: "Does the simulator support refinery troubleshooting?",
    a: `Yes.

    Operators can investigate abnormal temperatures, pressure deviations, poor recoveries, equipment trips and inefficient process conditions.

    This helps develop systematic troubleshooting skills.

    The simulator encourages users to understand process cause-and-effect relationships rather than simply memorizing operating procedures.`
},

{
    q: "Why are KPIs important in the refinery?",
    a: `Key Performance Indicators (KPIs) provide measurable information about refinery operation.

    Examples include recovery efficiency, energy efficiency, equipment utilization, product quality and production performance.

    Monitoring KPIs allows operators to identify opportunities for process improvement.

    The Digital Twin continuously updates these indicators throughout plant operation.`
},

{
    q: "Can I study heat exchanger performance?",
    a: `Yes.

    Heat exchanger performance is continuously monitored using inlet temperatures, outlet temperatures, heat transfer effectiveness and thermal efficiency.

    These values help users understand energy recovery within the refinery.

    Heat exchanger analysis is integrated into several laboratory reports throughout the Digital Twin.`
},

{
    q: "Does the simulator teach process instrumentation?",
    a: `Yes.

    Users interact with transmitters, indicators, control valves, pumps and process instruments exactly as they would inside a refinery control room.

    Instrument tags follow industrial naming conventions commonly used in DCS environments.

    This improves familiarity with refinery instrumentation and process monitoring systems.`
},

{
    q: "Can this Digital Twin be expanded in the future?",
    a: `Yes.

    The NK Refinery Digital Twin has been designed with a modular architecture.

    Additional refinery units, utilities, laboratory systems, advanced process control, predictive maintenance and econometric analysis modules can be integrated without redesigning the existing system.

    This allows the platform to continue growing alongside industrial training requirements.`
},


  ];

  const [activeIndex, setActiveIndex] = useState(0);

  // ================= EARLY RETURN (AFTER HOOKS) =================
  if (!open) return null;

  return (
    <div
      className="faq-overlay"
      onMouseMove={onDrag}
      onMouseLeave={stopDrag}
      onMouseUp={stopDrag}
    >
      <div
        className="faq-window"
        style={{
          left: pos.x,
          top: pos.y,
          position: "absolute",
        }}
      >
        {/* HEADER */}
        <div className="faq-header" onMouseDown={startDrag}>
          <div className="faq-title-center">
            <div className="faq-main-title">
              NK TECH INDUSTRIAL DCS AND DIGITAL TWIN SOLUTION
            </div>
            <div className="faq-sub-title">
              FREQUENTLY ASKED QUESTIONS (FAQ)
            </div>
          </div>

          <button className="faq-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="faq-body">

          {/* LEFT */}
          <div className="faq-left">
            {faqData.map((item, index) => (
              <div
                key={index}
                className={`faq-question ${activeIndex === index ? "active" : ""}`}
                onClick={() => setActiveIndex(index)}
              >
                {item.q}
              </div>
            ))}
          </div>

          {/* RIGHT */}
          <div className="faq-right">
            <div className="faq-answer">
              {faqData[activeIndex].a}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default FAQ;